import {
    useState,
    PropsWithChildren,
    useEffect,
    useEffectEvent,
    useMemo,
    useRef,
} from "react";
import {
    InvoiceViewOptionGroup,
    InvoiceViewOptions,
} from "@common/types/invoice";
import { useInvoicesPageFiltersCache } from "@hooks/view/useInvoicesPageFiltersCache";
import { useInvoicesPageViewOptionsCache } from "@hooks/view/useInvoicesPageViewOptionsCache";
import { JsonParam, StringParam, useQueryParams } from "use-query-params";
import { z } from "zod";
import { defaultInvoiceStatuses } from "@components/pageHeader/filters/lib";
import { useInvoiceStats } from "@api/invoice.api";
import {
    resolveInvoiceDeepLinkFilters,
    resolveInvoiceDeepLinkView,
} from "@components/invoices/lib/invoicesUrl";
import {
    InvoicesViewContext,
    InvoiceViewFilters,
    toInvoiceApiFilters,
} from "./InvoicesViewContext";

const invoiceUrlFiltersSchema = z.object({
    invoiceIds: z.array(z.string()).optional(),
    catalogItemIds: z.array(z.string()).optional(),
    entityIds: z.array(z.number()).optional(),
});

export const InvoicesViewProvider: React.FC<PropsWithChildren> = ({
    children,
}) => {
    const [cachedFilters, setCachedFilters] = useInvoicesPageFiltersCache();
    const [cachedViewOptions, setCachedViewOptions] =
        useInvoicesPageViewOptionsCache();

    const [{ filters: urlFilters, invoiceId, groupBy }, setQueryParams] =
        useQueryParams({
            filters: JsonParam,
            invoiceId: StringParam,
            groupBy: StringParam,
        });
    const groupByResult = z
        .nativeEnum(InvoiceViewOptionGroup)
        .safeParse(groupBy);
    const urlGroupBy = groupByResult.success ? groupByResult.data : undefined;

    const parsedUrlFilters = useMemo(() => {
        const filtersResult = urlFilters
            ? invoiceUrlFiltersSchema.safeParse(urlFilters)
            : null;
        const invoiceIdResult = invoiceId
            ? z.string().uuid().safeParse(invoiceId)
            : null;
        return resolveInvoiceDeepLinkFilters({
            filters:
                filtersResult?.success === true
                    ? filtersResult.data
                    : undefined,
            invoiceId:
                invoiceIdResult?.success === true
                    ? invoiceIdResult.data
                    : undefined,
        });
    }, [invoiceId, urlFilters]);

    const initialIdsRef = useRef(
        parsedUrlFilters?.invoiceIds ?? parsedUrlFilters?.catalogItemIds,
    );
    const initialIds = initialIdsRef.current;

    const [filters, setFilters] = useState<InvoiceViewFilters | undefined>(
        () => {
            const cachedApiFilters = { ...cachedFilters };
            delete cachedApiFilters.invoiceStatuses;
            if (
                parsedUrlFilters?.invoiceIds ||
                parsedUrlFilters?.catalogItemIds ||
                parsedUrlFilters?.entityIds
            ) {
                return {
                    externalInvoiceStatuses: defaultInvoiceStatuses,
                    ...(parsedUrlFilters.invoiceIds && {
                        invoiceIds: parsedUrlFilters.invoiceIds,
                    }),
                    ...(parsedUrlFilters.catalogItemIds && {
                        catalogItemIds: parsedUrlFilters.catalogItemIds,
                    }),
                    ...(parsedUrlFilters.entityIds && {
                        entityIds: parsedUrlFilters.entityIds,
                    }),
                };
            }
            return {
                externalInvoiceStatuses: defaultInvoiceStatuses,
                ...cachedApiFilters,
            };
        },
    );

    const clearUrlFiltersOnMount = useEffectEvent(() => {
        if (initialIds?.length || parsedUrlFilters?.entityIds) {
            setQueryParams({ filters: undefined }, "replaceIn");
        }
    });
    useEffect(() => {
        clearUrlFiltersOnMount();
    }, []);

    const [viewOption, setViewOption] = useState<InvoiceViewOptions>(() =>
        resolveInvoiceDeepLinkView({
            filters: parsedUrlFilters,
            groupBy: urlGroupBy,
            fallback: cachedViewOptions,
        }),
    );

    useEffect(() => {
        setCachedFilters(toInvoiceApiFilters(filters));
    }, [filters, setCachedFilters]);

    useEffect(() => {
        setCachedViewOptions(viewOption);
    }, [viewOption, setCachedViewOptions]);

    const { data: invoiceStats } = useInvoiceStats({
        filters: toInvoiceApiFilters(filters),
    });

    const contextValue = useMemo(
        () => ({
            filters,
            setFilters,
            viewOption,
            setViewOption,
            invoiceStats: invoiceStats?.data,
            initialIds,
        }),
        [
            filters,
            setFilters,
            viewOption,
            setViewOption,
            invoiceStats?.data,
            initialIds,
        ],
    );

    return (
        <InvoicesViewContext.Provider value={contextValue}>
            {children}
        </InvoicesViewContext.Provider>
    );
};
